import { useEffect, useState } from 'react';
import { api } from '@/api/client';
import { buildEduCoreUrl, type EduCorePath } from '@/lib/educoreLinks';

/**
 * The institution's EduCore launch origin, if one is entitled and configured.
 *
 * Fetched once per institution from the member products route — the same source
 * ProductsPage uses — so a bursar who can see EduCore there can also deep-link
 * from a KPI. Absent or failed → null, and the UI falls back to `/products`.
 */
export function useEduCoreLaunchUrl(institutionId: string | undefined): {
  launchUrl: string | null;
  ready: boolean;
  href: (path: EduCorePath) => string | null;
} {
  const [launchUrl, setLaunchUrl] = useState<string | null>(null);
  const [ready, setReady] = useState(!institutionId);

  useEffect(() => {
    if (!institutionId) {
      setLaunchUrl(null);
      setReady(true);
      return;
    }

    let cancelled = false;
    setReady(false);

    api.products
      .forInstitution(institutionId)
      .then((products) => {
        if (cancelled) return;
        const list = Array.isArray(products) ? products : [];
        const educore = list.find(
          (product) =>
            product.code === 'EDUCORE' &&
            product.status === 'AVAILABLE' &&
            Boolean(product.launchUrl),
        );
        setLaunchUrl(educore?.launchUrl ?? null);
      })
      .catch(() => {
        if (!cancelled) setLaunchUrl(null);
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [institutionId]);

  return {
    launchUrl,
    ready,
    href: (path) => buildEduCoreUrl(launchUrl, path),
  };
}
